import React, { useMemo } from "react"
import Select from "~/components/reusable/Select"
import { useCollectionSkuExtraDataValues, useExtraDataValues } from "~/hooks/useSku"
import { OTHER_GROUP_LABEL, RELEVANT_GROUP_LABEL } from "~/utils/groupedFilterOptions"

type EnumOption = { value: string; label: string }
type EnumOptionGroup = { label: string; options: EnumOption[] }

interface FilterExternalEnumSelectProps {
  columnName: string
  columnValue: string
  onValueChange: (value: string) => void
  isMulti?: boolean
  selectedValues?: string[]
  onValuesChange?: (values: string[]) => void
  autoFocus?: boolean
  // When provided, options present in this set render under a "Relevant" group;
  // the rest fall under "Other options". Falls back to flat if either group is empty.
  relevantValues?: Set<string>
}

export const FilterExternalEnumSelect: React.FC<FilterExternalEnumSelectProps> = React.memo(
  ({
    columnName,
    columnValue,
    onValueChange,
    isMulti = false,
    selectedValues = [],
    onValuesChange,
    autoFocus = true, // was always true before, but now it's optional
    relevantValues,
  }) => {
    const cleanedString = columnName
      .replace(/\(extradata\)/g, "")
      .replace(/\(collection-extradata\)/g, "")
      .trim()
    const { data: externalEnumOptions } = useExtraDataValues(cleanedString)
    const { data: externalCollectionEnumOptions } = useCollectionSkuExtraDataValues(cleanedString)

    const flatOptions = useMemo<EnumOption[]>(() => {
      if (!externalEnumOptions || !externalCollectionEnumOptions) return []
      return [...externalEnumOptions, ...externalCollectionEnumOptions]
        .filter((x) => x.value)
        .map((option) => ({
          value: option.value,
          label: option.value,
        }))
    }, [externalEnumOptions, externalCollectionEnumOptions])

    const groupedOptions = useMemo<EnumOptionGroup[] | null>(() => {
      if (!relevantValues || relevantValues.size === 0) return null
      const relevant: EnumOption[] = []
      const other: EnumOption[] = []
      for (const opt of flatOptions) {
        if (relevantValues.has(opt.value)) relevant.push(opt)
        else other.push(opt)
      }
      if (relevant.length === 0 || other.length === 0) return null
      return [
        { label: RELEVANT_GROUP_LABEL, options: relevant },
        { label: OTHER_GROUP_LABEL, options: other },
      ]
    }, [flatOptions, relevantValues])

    const optionsForSelect = groupedOptions ?? flatOptions

    if (isMulti) {
      return (
        <Select
          isMulti
          options={optionsForSelect}
          value={selectedValues.map((value) => ({ value, label: value }))}
          onChange={(operator) => onValuesChange?.(operator?.map((x) => x.value) ?? [])}
          isDisabled={!flatOptions.length}
          placeholder={flatOptions.length ? "Select values" : "No values"}
          openMenuOnFocus
          autoFocus={autoFocus}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
        />
      )
    }

    return (
      <Select
        options={optionsForSelect}
        getOptionValue={(enumItem) => enumItem.value}
        getOptionLabel={(enumItem) => enumItem.value}
        onChange={(selectedOption) => {
          onValueChange(selectedOption?.value ?? "")
        }}
        value={columnValue ? { value: columnValue, label: columnValue } : undefined}
        openMenuOnFocus
        autoFocus={autoFocus}
      />
    )
  },
)

FilterExternalEnumSelect.displayName = "FilterExternalEnumSelect"
